import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ProtectedRoute from './App/Shared/Guards/ProtectedRoutes.tsx';
import Loader from './App/Shared/Components/Loader.tsx';

import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './App/Authentication/Login/Login.tsx';
import Otp from './App/Authentication/Otp/Otp.tsx';
import SelectContact from './App/Authentication/SelectContact/SelectContact.tsx';
import StudentLogin from './App/Authentication/StudentLogin/StudentLogin.tsx';
import PageNotFound from './App/PageNotFound/PageNotFoundPage.tsx';

// Heavy / non-entry route trees are code-split so they stay out of the initial
// bundle.
const Dashboard = lazy(() => import('./App/Layout/MainRoutes.tsx'));
const ParentRoutes = lazy(() => import('./App/ParentApp/Routes.tsx'));
const AssessmentPlayer = lazy(() => import('./App/Assessments/StudentAssessments/AssessmentPlayer.tsx'));
const PublicPayLinkPage = lazy(() => import('./App/ParentApp/PaymentProcess/PublicPayLinkPage.tsx'));
const ClaimPage = lazy(() => import('./App/Authentication/Claim/ClaimPage.tsx'));
const PasswordResetPage = lazy(
  () => import('./App/Authentication/PasswordReset/PasswordResetPage.tsx')
);
const PublicReceiptPage = lazy(() => import('./App/ParentApp/PublicReceipt/PublicReceiptPage.tsx'));

// The canonical path moved to /dashboard/assessment/take/:id, but "Share Link" URLs
// generated before that change (AssessmentDetail.tsx) point at the old path — keep
// it resolvable so already-shared links don't break.
function LegacyAssessmentRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/assessment/take/${id}`} replace />;
}

function App() {
  return (
    <>
      <Suspense fallback={<Loader heightScreen />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/student/login" element={<StudentLogin />} />
        <Route path="/otp/select" element={<SelectContact />} />
        <Route path="/otp" element={<Otp />} />
        <Route path="/pay/:token/*" element={<PublicPayLinkPage kind="fee" />} />
        <Route path="/pay-invoice/:token/*" element={<PublicPayLinkPage kind="invoice" />} />
        {/* Public digital receipt: no login; the secret is in the #fragment. */}
        <Route path="/r" element={<PublicReceiptPage />} />
        {/* Invite claim: no login; the token is in the #fragment. */}
        <Route path="/claim" element={<ClaimPage />} />
        {/* Password reset link: no login; the token is in the #fragment. */}
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/parent/*" element={<ParentRoutes />} />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/404" element={<PageNotFound />} />
        <Route path="/dashboard/assessment/take/:id" element={<AssessmentPlayer />} />
        <Route path="/assessment/take/:id" element={<LegacyAssessmentRedirect />} />
        <Route path="/*" element={<Navigate to="/404" />} />
      </Routes>
      </Suspense>
      <ToastContainer
        position="top-right"
        theme="light"
        transition={Slide}
        hideProgressBar={false}
        newestOnTop={false}
        closeButton
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default App;
